import React, { useState } from 'react';
import { Icon, Link, Loader, Typography } from '@saviynt/design-system';

import { useLocalization } from '../../../../hooks';
import { MESSAGE_SNIPPET_KEYS } from '../../constants/messageSnippets';
import type { MessageSubType } from '../../data-layer/types';
import { useApplicationName } from '../../hooks';

import './Responses.css';

interface ResponseButton {
  label: string;
  iconKind?: string;
  onClick?: (value: string) => void;
}

interface ResponsesProps {
  type: MessageSubType;
  loader?: boolean;
  buttons?: ResponseButton[];
  values?: string[];
  text?: string;
  onStepClick?: (stepName: string) => void;

  // Custom text override
}

/**
 * Responses Component
 * Single responsibility: Display response messages with loader and optional buttons
 */
function Responses({
  type,
  loader = false,
  buttons = [],
  values,
  text,
  onStepClick,
}: ResponsesProps) {
  const formatMessage = useLocalization();
  const applicationName = useApplicationName();
  const [disabledButtons, setDisabledButtons] = useState<boolean>(false);

  const stepDisplayNames: Record<string, string> = {
    CreateAccount: 'Create Account',
    UpdateAccount: 'Update Account',
    EnableAccount: 'Enable Account',
    DisableAccount: 'Disable Account',
    RemoveAccount: 'Remove Account',
    AddAccess: 'Add Access',
  };

  const getMessage = () => {
    // Use custom text if provided
    if (text) {
      return text;
    }

    // Handle rich content for specific subTypes
    if (type === 'threeButtonsHelperText') {
      return (
        <span>
          You can provide additional instructions to help me perform this action
          in the {applicationName} more accurately.
          <br />
          For example, you might say: &apos;Enter username and password → click
          Login&apos;.
          <br />
          Select one of the option below to get started! Click{' '}
          <Link
            href='https://docs.saviyntcloud.com/bundle/EIC-Admin-25/page/Content/Chapter04-Application-Management-and-Onboarding/Onboard-Manage-App-v2022/Enrolling-Disconnected-Applications-v2022.htm#Configur2'
            text='here'
            target='_blank'
            color='brand'
            size='medium'
          />{' '}
          to see how to record using your browser.
        </span>
      );
    }

    const localizationKey = MESSAGE_SNIPPET_KEYS[type];

    // Fallback messages for development
    const fallbackMessages: Record<MessageSubType, string> = {
      start: "Let's get started with your integration setup!",
      analyzing:
        "Thanks for sharing. I'm analyzing your file, and this will take a few minutes",
      createAccount: `You can provide additional instructions to help me perform this action in the {applicationName} more accurately.\nFor example, you might say: 'Navigate to Administration → User Management to create a user' or 'Navigate to Administration → Reports to download accounts'.\nDo you want to provide additional information?`,
      updateAccount: `You can provide additional instructions to help me perform this action in the {applicationName} more accurately.\nFor example, you might say: 'Navigate to Administration → User Management to create a user' or 'Navigate to Administration → Reports to download accounts'.\nDo you want to provide additional information?`,
      enableAccount: `You can provide additional instructions to help me perform this action in the {applicationName} more accurately.\nFor example, you might say: 'Navigate to Administration → User Management to create a user' or 'Navigate to Administration → Reports to download accounts'.\nDo you want to provide additional information?`,
      disableAccount: `You can provide additional instructions to help me perform this action in the {applicationName} more accurately.\nFor example, you might say: 'Navigate to Administration → User Management to create a user' or 'Navigate to Administration → Reports to download accounts'.\nDo you want to provide additional information?`,
      removeAccount: `You can provide additional instructions to help me perform this action in the {applicationName} more accurately.\nFor example, you might say: 'Navigate to Administration → User Management to create a user' or 'Navigate to Administration → Reports to download accounts'.\nDo you want to provide additional information?`,
      fileExtraction: 'Extracting file information...',
      movingNext: 'Moving to next step...',
      skipTransition: 'Moving to next step...',
      generalToConnection: 'Moving on to next step to setup a connection',
      success: 'Success!',
      createAccountHelperText: 'Need help creating an account?',
      basic_config: 'Basic configuration',
      connection_config: "I'll assist you with setting up connection details",
      create_account_form: 'Create account form',
      update_account_form: 'Update account form',
      enable_account_form: 'Enable account form',
      disable_account_form: 'Disable account form',
      remove_account_form: 'Remove account form',
      text_input: 'Text input',
      file_upload: 'File upload',
      user_choice: 'User choice',
      file_display: 'File display',
      connectionStep: 'Great! Moving to connection setup...',
      connectionStepAssistInfo: `You can provide additional instructions to help me perform this action in the {applicationName} more accurately.\nFor example, you might say: 'Enter username and password → click Login'.\nSelect one of the option below to get started!`,
      selectOneOfOptions: 'Select one of the options below to get started!',
      userResponseYes: 'Yes',
      userResponseNo: 'No',
      connectionSuccessMovingNext:
        'Connection successful! Moving to next step...',
      createAccountNextStep:
        "Connection successful! Now let's set up account management.",
      createAccountAssist:
        "Ok. I'll assist you with setting up creating an account.\nSpecify the external app fields to populate (as in the Create User UI) and map them to corresponding Saviynt attribute",
      additionalInformation: 'Do you want to provide additional information?',
      confirmOnboard:
        "Great! I've configured the Create Account template. Do you want to proceed with onboarding?",
      provideAdditionalInfo: 'Great! Please provide additional information.',
      movingToNextStep: 'Thanks for sharing the instructions...',
      movingToNextAccStep: 'Moving to next account step...',
      movingToNextStepWithSkip: 'Thanks. Moving to next step...',
      proceedToUpdateAccount:
        'Great! Moving on to next step to update an account',
      updateAccountStepAssistInfo:
        "The Saviynt and Application fields are pre-populated based on your Create Account configuration. You can update these fields as they appear in the application's Update User form",
      updateAccountConfirmationInfo:
        'Hey, I noticed you provided additional information while creating the account. Would you like to share similar details for updating the account as well?',
      allAccountGenericConfirmation:
        "I noticed you've provided instructions for the earlier use cases. Would you like to add instructions for this use case as well?",
      enableAccountGenericConfirmation:
        "I noticed you've provided instructions for the earlier use cases. Would you like to add instructions for enabling the user as well?",
      disableAccountGenericConfirmation:
        "I noticed you've provided instructions for the earlier use cases. Would you like to add instructions for disabling the user as well?",

      removeAccountGenericConfirmation:
        "I noticed you've provided instructions for the earlier use cases. Would you like to add instructions for removing the user as well?",

      enableAccountStepAssistInfo:
        'Specify the user field used to find and enable the user in the application, then map it to the relevant Saviynt attribute (e.g., Username)',
      disableAccountStepAssistInfo:
        'Specify the user field used to find and disable the user in the application, then map it to the relevant Saviynt attribute (e.g., Username)',
      removeAccountStepAssistInfo:
        'Specify the user field used to find and remove the user in the application, then map it to the relevant Saviynt attribute (e.g., Username)',
      proceedToEnableAccount:
        'Great! Moving on to next step to enable an account',
      proceedToDisableAccount:
        'Great! Moving on to next step to disable an account',
      proceedToRemoveAccount:
        'Great! Moving on to next step to remove an account',
      movingToAccessAccount:
        "Great! Now let's configure access management for your application.",
      accessAccountMessage:
        "Perfect! Let's set up access entitlements. Please specify the entitlement types that users can be granted access to in your application.",
      accessAttributeMapping:
        "Excellent! Now let's map these entitlements to the appropriate application fields.",
      attributeMappingApplicationFields: '',
      attributeMappingStart: 'Atrribute Value',
      attributeMappingNext: 'attribute mapping next',
      add_access_form: 'add access form',
      attributeMappingAdditionalInfo:
        'Do you want to provide additional information for {value0}?',
      removeAccessNextStep: 'remove access',
      remove_access_form: 'remove access form',
      removeAccess: 'remove access attribute maping',
      removeAccessMapping: 'remove access mapping',
      removeAccessMappingInfo: 'remove access mapping info',
      finalRecommendations:
        'Based on your configuration, here are the recommended next steps:',
      finalConfirmation: 'Final confirmation message',
      finalConfirmationRedirection: 'Let’s goooo! Onboarding your application',
      error: 'An error occurred. Please try again.',
      basicUpdateSuccess:
        'You have updated the Application Details. I will add to the Configuration.',
      createAccountUpdateSuccess:
        'You have updated the Create Account configuration. I will add to the Configuration.',
      updateAccountUpdateSuccess:
        'You have updated the Update Account configuration. I will add to the Configuration.',
      enableAccountUpdateSuccess:
        'You have updated the Enable Account configuration. I will add to the Configuration.',
      disableAccountUpdateSuccess:
        'You have updated the Disable Account configuration. I will add to the Configuration.',
      removeAccountUpdateSuccess:
        'You have updated the Remove Account configuration. I will add to the Configuration.',
      addAccessUpdateSuccess:
        'You have updated the Add Access Entitlements. I will add to the Configuration.',
      removeAccessUpdateSuccess:
        'You have updated the Remove Access Entitlements. I will add to the Configuration.',
      instruction_prompt:
        'Please follow the instructions provided below to complete this step.',
      addAccessEditPhase: 'Do you want to provide additional information?',
      allEntitlementDone: 'All Entitlement Done',
      promptInstructions:
        'You have already provided instructions for this step.',
      stepHelperPrompt: 'How would you like to proceed with this step?',
      threeButtonsHelperText:
        'You can provide additional instructions to help me perform this action more accurately.',
      removeAccessEditInfo:
        '\n Here are the details of remove access configuration',
      addAccessInfoEditMode: '',
      removeAccessSkipped: '',
    };

    // Use localization service like Copilot1 - fallback if no localization key
    if (localizationKey) {
      return formatMessage(
        localizationKey,
        fallbackMessages[type] || 'Processing...',
        {
          applicationName,
          ...Object.fromEntries(values?.map((v, i) => [`value${i}`, v]) || []),
        }
      );
    }

    // Return fallback message directly if no localization key
    let fallbackMessage = fallbackMessages[type] || 'Processing...';

    // Apply applicationName replacement
    fallbackMessage = fallbackMessage.replace(
      /\{applicationName\}/g,
      applicationName
    );

    // Apply value replacements to fallback message
    if (values && values.length > 0) {
      values.forEach((value, index) => {
        fallbackMessage = fallbackMessage.replace(
          new RegExp(`\\{value${index}\\}`, 'g'),
          value
        );
      });
    }

    return fallbackMessage;
  };

  return (
    <div className='Integrations'>
      <div className='Integrations-Responses'>
        <span>
          <Icon
            className='Integrations-Responses-Icon'
            kind='SaviCompanion'
            color='--color-foreground-savi-medium'
          />
        </span>
        <Typography
          className='Integrations-Responses-Text'
          kind='body2'
          desktop='body2-d'
          mobile='body2-d'>
          {loader ? (
            <Loader
              className='Integrations-Responses-Text-Loader'
              kind='dots'
              format='inline'
              color='info'
            />
          ) : null}
          {getMessage()}
        </Typography>
      </div>
      {type === 'finalRecommendations' &&
        values &&
        values.length > 0 &&
        values[0] !== 'None' && (
          <div
            className='Integrations-Responses-Chips'
            style={{
              marginTop: '12px',
              display: 'flex',
              flexWrap: 'wrap',
              gap: '8px',
              marginLeft: '2rem',
            }}>
            {values[0].split(', ').map((step, index) => (
              <button
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                type='button'
                className='Integrations-Responses-Chip'
                onClick={() => onStepClick?.(step)}
                style={{
                  display: 'flex',
                  padding: '4px 8px',
                  backgroundColor: 'var(--color-background-savi-subtle)',
                  borderRadius: '12px',
                  fontSize: '12px',
                  color: '#333',
                  border: '1px solid rgba(249, 238, 252, 1)',
                  cursor: 'pointer',
                  gap: '4px',
                  alignItems: 'center',
                }}>
                {stepDisplayNames[step] || step}
              </button>
            ))}
          </div>
        )}
      {buttons.length > 0 ? (
        <div className='Integrations-Buttons'>
          {buttons.map((button) => (
            <button
              key={button.label}
              type='button'
              disabled={disabledButtons}
              className='Integrations-Buttons-Response'
              onClick={() => {
                button.onClick?.(button.label);
                setDisabledButtons(true);
              }}>
              {button.iconKind && (
                <Icon
                  kind={button.iconKind}
                  color='--color-foreground-savi-medium'
                />
              )}
              {button.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

Responses.defaultProps = {
  loader: false,
  buttons: [],
  values: [],
  text: undefined,
  onStepClick: undefined,
};

export default Responses;
